/**
 * ParentOnboardingWizard — wizard chào mừng phụ huynh đăng nhập lần đầu.
 *
 * Vấn đề giải quyết (Aha < 60s): phụ huynh mới vào thấy workspace RỖNG (empty state)
 * → dễ rời bỏ. Wizard dẫn họ tới hành động kích hoạt đầu tiên: THÊM CON (dữ liệu thật)
 * → workspace có dữ liệu ngay → hiểu giá trị nhanh.
 *
 * Hiển thị khi: chưa hoàn thành onboarding (localStorage) VÀ chưa có con nào.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronRight, X, Sparkles, School, Share2, LayoutDashboard, Loader2 } from 'lucide-react';
import { getChildren, syncResource } from '../../../services/apiService';
import { UI_CONFIG } from '../../../config';

const DONE_KEY = 'bookai_parent_onboarding_done';

export const ParentOnboardingWizard: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<'female' | 'male' | 'other'>('female');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (localStorage.getItem(DONE_KEY)) return;
    getChildren()
      .then(list => {
        if (list && list.length > 0) { localStorage.setItem(DONE_KEY, '1'); return; }
        setVisible(true);
        setTimeout(() => nameRef.current?.focus(), 120);
      })
      .catch(() => { setVisible(true); });
  }, []);

  const dismiss = () => { localStorage.setItem(DONE_KEY, '1'); setVisible(false); };

  const handleCreateChild = async () => {
    if (!name.trim()) { setErr('Vui lòng nhập tên con.'); return; }
    setErr('');
    setSaving(true);
    const res = await syncResource('children', 'POST', { name: name.trim(), dob, gender });
    setSaving(false);
    if (res) {
      localStorage.setItem(DONE_KEY, '1');
      setStep(3);
    } else {
      setErr('Không lưu được. Vui lòng thử lại.');
    }
  };

  // Điều hướng bằng full-reload để WorkspaceProvider nạp lại danh sách con mới tạo.
  const go = (path: string) => { localStorage.setItem(DONE_KEY, '1'); window.location.assign(path); };

  if (!visible) return null;

  const progressWidth = step === 1 ? '33%' : step === 2 ? '66%' : '100%';

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="h-1.5 bg-slate-100">
          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: progressWidth }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-2">
          <div className="flex items-center gap-2.5">
            {UI_CONFIG.APP_LOGO_URL ? (
              <img src={UI_CONFIG.APP_LOGO_URL} alt={UI_CONFIG.APP_NAME} className="w-8 h-8 rounded-xl object-contain" />
            ) : (
              <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-md">
                {(UI_CONFIG.APP_NAME || 'B').charAt(0)}
              </div>
            )}
            <span className="font-bold text-slate-800 text-sm">{UI_CONFIG.APP_NAME || 'BooKAI'}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Bước {step} / 3</span>
            <button onClick={dismiss} className="p-1 rounded-lg text-slate-300 hover:text-slate-500 transition-colors" title="Bỏ qua">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* STEP 1: Welcome */}
        {step === 1 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5">
              <Sparkles className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Chào mừng phụ huynh đến {UI_CONFIG.APP_NAME || 'BooKAI'}!</h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Cùng đồng hành việc học của con: theo dõi điểm số, bài tập, lộ trình học tập và trợ lý AI. Chỉ mất 30 giây để bắt đầu.
            </p>
            <div className="space-y-2.5 mb-6">
              {[
                { emoji: '📈', text: 'Theo dõi tiến độ & điểm số của con tự động' },
                { emoji: '📝', text: 'Xem bài tập giáo viên giao và đồng hành con làm' },
                { emoji: '🤖', text: 'Trợ lý AI tư vấn cách giúp con học tốt hơn' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3.5 py-2.5">
                  <span className="text-lg">{f.emoji}</span>
                  <span className="text-xs font-semibold text-slate-600">{f.text}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(2)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2">
              Bắt đầu <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* STEP 2: Add child (real data) */}
        {step === 2 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
              <Check className="w-7 h-7 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Thêm tài khoản con</h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Tạo hồ sơ con để bắt đầu theo dõi. Bạn có thể thêm con khác bất kỳ lúc nào.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Tên con <span className="text-rose-500">*</span></label>
                <input ref={nameRef} type="text" maxLength={60} placeholder="Nhập tên đầy đủ của con…"
                  value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateChild()}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Ngày sinh (tùy chọn)</label>
                <input type="date" value={dob} onChange={e => setDob(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Giới tính</label>
                <div className="flex gap-2">
                  {(['female', 'male', 'other'] as const).map(g => (
                    <button key={g} type="button" onClick={() => setGender(g)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${gender === g ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-600 hover:border-indigo-300'}`}>
                      {g === 'female' ? 'Nữ' : g === 'male' ? 'Nam' : 'Khác'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {err && <p className="text-xs text-rose-500 mt-3">{err}</p>}
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="flex-1 border border-slate-200 text-slate-600 font-bold py-3 rounded-xl text-sm hover:bg-slate-50 transition-all">Quay lại</button>
              <button onClick={handleCreateChild} disabled={saving}
                className="flex-[2] bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                {saving ? 'Đang tạo...' : 'Tạo & tiếp tục'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Done + next actions */}
        {step === 3 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mb-5 shadow-lg shadow-indigo-100">
              <Check className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Đã thêm {name.trim()}! 🎉</h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Bước tiếp theo để khai thác tối đa BooKAI cho con:
            </p>
            <div className="space-y-2.5 mb-6">
              <button onClick={() => go('/workspace/phu-huynh/quan-ly-con')}
                className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all text-left">
                <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0"><School className="w-4 h-4" /></div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-800">Liên kết lớp học của con</p>
                  <p className="text-[11px] text-slate-400">Để điểm số & bài tập tự cập nhật</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </button>
              <button onClick={() => go('/workspace/phu-huynh/quan-ly-con')}
                className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all text-left">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0"><Share2 className="w-4 h-4" /></div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-800">Mời giáo viên của con</p>
                  <p className="text-[11px] text-slate-400">Mời thầy/cô tham gia BooKAI</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </button>
            </div>
            <button onClick={() => go('/workspace/phu-huynh')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3.5 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2">
              <LayoutDashboard className="w-4 h-4" /> Vào không gian phụ huynh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentOnboardingWizard;
